import BaseBehavior from '../../../framework/base/base';
import { debounce } from '../../../shared/utilities/debounce';
import { filterQuery, regEx } from '../../../shared/utilities/filterQuery';
import { onEffectEndEvent } from '../../../shared/utilities/effectsEndEventHandler';
import { objectToQueryString } from '../../../shared/utilities/objectToQueryString';
import { repaint } from '../../../shared/utilities/repaint-fix';
import { screenSmallerThan } from '../../../shared/utilities/screenSize';
import { addEventListenerOnce } from '../../../shared/utilities/addListenerOnce';
import { initProvider } from '../../../shared/utilities/search-provider/searchProvider';
import { showElement } from '../../../shared/utilities/show-hide';
import trackSearchResult from '../../../shared/utilities/search-tracking/search-tracking';

export default class Navigation extends BaseBehavior {
	constructor(element) {
		super(element);
		this.initDomCache();
		this.selectedNav = null;
		this.isSearchActive = false;
		this.fetching = { internal: false, external: false };
		this.lastValidQuery = '';
		this.addEventListeners();
		this.config = {
			breakpoint: 1024,
			desktopHeaderHeight: 76,
			mobileHeaderHeight: 62
		};

		if (document.querySelector('.v2-cookie-disclaimer--top')) {
			const setNavigationPosition = this.setNavigationPosition.bind(this);
			setNavigationPosition();
			this.subscribe('cookie/height/change', setNavigationPosition);
			addEventListenerOnce(window, 'load', setNavigationPosition);
		}

		if (this.dom.searchContainer?.hasAttribute('data-suggestions')) {
			this.setupSearchSuggestions();
		}
	}

	setNavigationPosition() {
		repaint(this.dom.header);
		const cookieHeight = document.querySelector('.v2-cookie-disclaimer').getBoundingClientRect()
			.height;
		let navHeight = cookieHeight;
		let navDropdownTop = cookieHeight;
		if (window.innerWidth < this.config.breakpoint) {
			navHeight += this.config.mobileHeaderHeight;
			navDropdownTop += this.config.mobileHeaderHeight;
		} else {
			navHeight += this.config.desktopHeaderHeight;
		}
		this.dom.header && (this.dom.header.style.top = cookieHeight + 'px');
		this.dom.navDropdown && (this.dom.navDropdown.style.top = navDropdownTop + 'px');
		const dropdownHeight = window.innerHeight - navDropdownTop;
		this.dom.navDropdown.style.height = dropdownHeight + 'px';
		if (screenSmallerThan('md'))
			this.dom.dropdownSearchContainer &&
				(this.dom.dropdownSearchContainer.style.height = dropdownHeight + 'px');
		this.element.style.height = navHeight + 'px';
	}

	handleCloseNavMenu() {
		this.element.classList.remove('navigation--open');
		if (this.selectedNav) {
			this.selectedNav.classList.remove('active');
			this.selectedNav.nextElementSibling.classList.remove('no-transition');
			this.selectedNav = null;
		}
	}

	focusSearchInput() {
		const { searchFormInput } = this.dom;
		searchFormInput.focus();
		// retry focus in-case transition has yet to end.
		onEffectEndEvent(searchFormInput, () => searchFormInput.focus());
	}

	handleOpenNavMenu(e) {
		e.stopPropagation();
		const navLink = e.target.closest('button.nav__link');
		if (navLink) {
			const isSelected = navLink.classList.contains('active');
			if (!isSelected) {
				this.element.classList.add('navigation--open');
				if (this.selectedNav) {
					this.selectedNav.classList.remove('active');
					this.selectedNav.nextElementSibling.classList.remove('no-transition'); // for animation when switching between two active elements
					navLink.nextElementSibling.classList.add('no-transition');
				}

				this.selectedNav = navLink;
				this.selectedNav.classList.add('active');
				this.focusSearchInput();
			} else {
				this.handleCloseNavMenu();
			}
		}
	}

	// hide fromSearch and show toSearch;
	switchSearchContainers({ from, to }) {
		from && from.classList.add('v2-hidden');
		to && to.classList.remove('v2-hidden');
	}

	handleSearch(e) {
		e.stopPropagation();
		this.element.classList.add('navigation--open');
		this.selectedNav && this.handleCloseNavMenu();
		this.dom.searchContainer.classList.add('active');
		this.isSearchActive = true;
		this.dom.noresults.classList.add('v2-hidden');
		this.switchSearchContainers({ from: this.dom.results, to: this.dom.quicklinks });
		this.focusSearchInput();
	}

	openMobileSubNav(evt) {
		const element = evt.target.closest('.nav-trigger');
		if (element && !element.classList.contains('nav-link')) {
			const trigger = evt.target.closest('.nav-trigger');
			this.dom.navItemsContainer.innerHTML = '';
			const nextTarget = this.element.querySelector('#' + trigger.dataset.target);
			this.dom.navItemsContainer.innerHTML = nextTarget.innerHTML;
			this.dom.navDropdown.classList.add('show');
			this.dom.navDropdown.classList.remove('hide');
		}
	}

	resetToInitialState(evt) {
		if (evt.target.closest('.navigate-back')) {
			this.dom.navDropdown.classList.remove('show');
			this.dom.navDropdown.classList.add('hide');
		}
	}

	handleIconClick(e) {
		if (e.target.closest('.icon--menu')) {
			this.element.classList.toggle('mobile-nav');
			this.dom.navDropdown.classList.remove('show');
			document.body.classList.toggle('v2-overflow-hidden'); // to stop scrolling main page behind navigation dropdown
		}
		if (e.target.closest('.icon--search')) {
			this.handleSearch(e);
		}
	}

	generateSuggestionItemElement(item, getMarkup) {
		const wrapper = document.createElement('div');
		const markup = getMarkup(item);
		const listItem = `<li class="item"><a href="${markup.url}">${markup.htmlTitle}</a></li>`;
		wrapper.innerHTML = listItem;
		return wrapper.firstElementChild;
	}

	getMarkup(item) {
		return {
			url: item.link,
			title: item.title,
			htmlTitle: item.htmlTitle,
			snippet: item.htmlSnippet
		};
	}

	triggerExternalSuggestions(query) {
		this.dom.externalResults.classList.add('v2-hidden');
		this.dom.externalResultsList.innerHTML = '';
		this.provider.reset();
		this.fetching.external = true;
		this.updateLoader();
		const request = this.provider.getRequest({ query });

		const option = this.provider.getOptions();
		fetch(request.url, option)
			.then(response => response.json())
			.then(data => {
				const response = this.provider.parseResponse(data);
				// assign external results DOM ref to provider compatible DOM(list)
				this.dom.list = this.dom.externalResultsList;

				if (response.queries.nextPage) {
					showElement(this.dom.showMoreWrapper);
				}
				if (response.items) {
					const fragment = document.createDocumentFragment();
					response.items.forEach(item =>
						fragment.appendChild(this.generateSuggestionItemElement(item, this.getMarkup))
					);
					this.dom.list.appendChild(fragment);
				} else {
					showElement(this.dom.noResults);
				}
				const totalResults = response.searchInformation.totalResults
					? +response.searchInformation.totalResults
					: 0;
				trackSearchResult(query, totalResults, 'All');
				this.dom.externalResultsList.childElementCount &&
					this.dom.externalResults.classList.remove('v2-hidden');
				// add input query param to view all link(we are expecting category query param to be present already)
				this.dom.externalViewAll.href = `${this.dom.externalViewAll.dataset.searchCategoryUrl}&q=${query}`;
			})
			.catch(e => {
				// eslint-disable-next-line no-console
				console.error(e);
			})
			.finally(() => {
				this.fetching.external = false;
				this.updateLoader();
				repaint(this.dom.results); // trigger reflow to solve IE bug
			});
	}

	triggerInternalSuggestions(query) {
		const {
			serviceurl,
			top,
			culture,
			db,
			env,
			assetids,
			commissiongroupids,
			namecategoryid,
			hideplansdata
		} = this.dom.internalResults.dataset;
		const queryParameters = objectToQueryString({
			keywords: query,
			top,
			culture,
			db,
			env,
			assetids,
			commissiongroupids,
			namecategoryid,
			hideplansdata
		});
		this.dom.internalResultsList.innerHTML = '';
		this.fetching.internal = true;

		this.updateLoader();
		this.dom.internalResults.classList.add('v2-hidden');

		fetch(`${serviceurl}?${queryParameters}`)
			.then(response => response.json())
			.then(response => {
				if (!response?.Data.length) return;
				this.dom.internalResultsList.innerHTML = this.buildInstrumentMarkup(response.Data);
				this.dom.internalViewAll.href = `${this.dom.internalViewAll.dataset.searchCategoryUrl}&q=${query}`;
				this.dom.internalResults.classList.remove('v2-hidden');
				trackSearchResult(query, response.Data.length, 'Instruments');
			})
			.catch(e => {
				// eslint-disable-next-line no-console
				console.error(e);
			})
			.finally(() => {
				this.fetching.internal = false;
				this.updateLoader();
				repaint(this.dom.results); // trigger reflow to solve IE bug
			});
	}

	updateLoader() {
		if (!this.fetching.internal && !this.fetching.external) {
			this.dom.spinner.classList.add('v2-hidden');
			if (
				this.dom.externalResultsList.childElementCount +
					this.dom.internalResultsList.childElementCount ===
				0
			) {
				this.dom.noresults.classList.remove('v2-hidden');
				this.switchSearchContainers({ from: this.dom.results, to: this.dom.quicklinks });
			}
		} else {
			this.dom.spinner.classList.remove('v2-hidden');
		}
	}

	buildInstrumentMarkup(data) {
		return data
			.map(item => {
				const icon = item.Instrument.HasAssetIcon
					? `<div class="instrument__logo instrument__logo--${item.Instrument.Icon}"></div>`
					: `<div class="instrument__logo v2-flag" style="background-image: url('/Content/assets/flags/inspiration/${item.Instrument.Country}.svg');"></div>`;

				return `<li>
						<div class="instrument">
							${icon}
							<div class="instrument__description">
								<div class="instrument__description-name">
									<span>${item.Instrument.Symbol}</span>
									<span class="instrument__description-exchange">${item.Instrument.Name}</span>
								</div>
							</div>
						</div>
					</li>`;
			})
			.join('');
	}

	handleSearchSuggestions([{ target }]) {
		if (target.value === this.lastValidQuery) return;

		if (target.value.trim().length >= target.dataset.minChars) {
			this.dom.eraseIcon.classList.remove('v2-hidden');
			const { internal, external } = this.validateInput(target.value);

			if (internal || external) {
				this.switchSearchContainers({ from: this.dom.quicklinks, to: this.dom.results });
				this.dom.noresults.classList.add('v2-hidden');
				this.lastValidQuery = target.value;
				!this.fetching.external && external && this.triggerExternalSuggestions(external);
				!this.fetching.internal && internal && this.triggerInternalSuggestions(internal);
			} else {
				this.switchSearchContainers({ from: this.dom.results, to: this.dom.quicklinks });
				this.dom.noresults.classList.remove('v2-hidden');
			}
		} else {
			this.lastValidQuery = '';
			this.dom.eraseIcon.classList.add('v2-hidden');
			this.switchSearchContainers({ from: this.dom.results, to: this.dom.quicklinks });
		}
	}

	setupSearchSuggestions() {
		// need to pick google / bing search config
		const config = JSON.parse(this.dom.searchConfig.innerHTML);
		this.searchConfig = config;
		this.searchProvider = config.provider;
		this.provider = initProvider(config);

		this.addEventListener(
			this.dom.searchFormInput,
			'keyup',
			debounce(this.handleSearchSuggestions.bind(this), 500)
		);
	}

	clearSearch() {
		this.lastValidQuery = '';
		this.dom.searchFormInput.value = '';
		this.dom.eraseIcon.classList.add('v2-hidden');
		this.switchSearchContainers({ from: this.dom.results, to: this.dom.quicklinks });
	}

	handleCloseNav(e) {
		// close search bar [only if it's navigation--open] only when clicked on outside of search and quicklinks container or 'X' icon
		const closeSearch = this.isSearchActive
			? e.target.closest('.navigation__search') && !e.target.closest('.navigation__search-close')
			: true;

		// close  Nav bar [only if it's navigation--open] only when clicked outside of Navigation and Navigation
		if (this.selectedNav && !e.target.closest('.header')) {
			this.handleCloseNavMenu();
		}

		if (!closeSearch) {
			this.isSearchActive = false;
			this.dom.searchContainer.classList.remove('active');
			this.dom.searchFormInput.value = '';
			this.lastValidQuery = '';
			this.handleCloseNavMenu();
		}
	}

	validateInput(value) {
		return {
			internal: filterQuery(value, regEx.INSTRUMENT_SEARCH),
			external: filterQuery(value, regEx.GOOGLE_SEARCH)
		};
	}

	checkAndApplyShadowOnScroll() {
		const { sessionStorage } = window;
		if (window.scrollY > 0) {
			sessionStorage?.setItem('isScrolled', 'true');
			document.documentElement.classList.add('isScrolled');
		} else {
			sessionStorage?.removeItem('isScrolled');
			document.documentElement.classList.remove('isScrolled');
		}
	}

	addEventListeners() {
		const delayApplyShadow = debounce(this.checkAndApplyShadowOnScroll.bind(this), 100, false);
		this.addEventListener(this.dom.navListContainer, 'click', this.openMobileSubNav.bind(this));
		this.addEventListener(this.dom.navItemsContainer, 'click', this.resetToInitialState.bind(this));
		this.addEventListener(this.dom.iconsContainer, 'click', this.handleIconClick.bind(this));
		this.addEventListener(this.dom.navMenu, 'click', this.handleOpenNavMenu.bind(this));
		// search related
		this.addEventListener(this.dom.eraseIcon, 'click', this.clearSearch.bind(this));
		this.addEventListener(document, 'click', this.handleCloseNav.bind(this));
		this.addEventListener(document.forms[0], 'submit', e => {
			const { internal, external } = this.validateInput(this.dom.searchFormInput.value);
			if (!internal && !external) {
				e.preventDefault();
			}
		});

		if (this.dom.searchContainer?.hasAttribute('data-suggestions')) {
			this.addEventListener(this.dom.searchFormInput, 'keyup', () => {
				debounce(this.handleSearchSuggestions.bind(this), 500);
			});
		}
		window.addEventListener('scroll', () => {
			delayApplyShadow();
		});
	}
}
